import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { barShare, formatValue, type FormatOptions } from '@/design'

import { share as formatShare } from './format'

/** One row of a share list: a figure, its share of the whole as the API gives it, and where it leads. */
export interface ShareBarRow {
  key: string
  label: string
  /** Major units (or a count with `format: 'integer'`); null draws no bar and reads `nullLabel`. */
  value: number | null
  /** The API's share of the whole, a fraction; null reads "—". */
  share: number | null
  href?: string
  /** A fixed row (other, fees, unattributed): set apart in muted ink. */
  fixed?: boolean
  nullLabel?: string
  detail?: string
}

/**
 * A ranking of one figure as horizontal bars, each row's label, figure and share as text
 * in text ink beside its bar (the list is the readout; the bars are decoration). A bar is
 * scaled to the largest row; zero, negative or missing draws none.
 */
export function ShareBars({
  caption,
  rows,
  money,
  format = 'currency',
  empty = 'Nothing in this period.',
}: {
  caption: string
  rows: readonly ShareBarRow[]
  money: FormatOptions
  format?: 'currency' | 'integer' | 'percent'
  empty?: string
}) {
  const top = Math.max(0, ...rows.map((row) => row.value ?? 0))
  return (
    <figure className="rv-bars">
      <figcaption className="ci-visually-hidden">{caption}</figcaption>
      {rows.length === 0 ? (
        <p className="rv-muted">{empty}</p>
      ) : (
        <ol className="rv-bars__rows">
          {rows.map((row) => {
            const width = barShare(row.value, top)
            return (
              <li key={row.key} className="rv-bars__row" data-row={row.key} data-fixed={row.fixed || undefined}>
                <span className="rv-bars__label" title={row.label}>
                  {row.href ? <Link to={row.href}>{row.label}</Link> : row.label}
                </span>
                <span className="rv-bars__figure">
                  <span className="ci-visually-hidden">: </span>
                  {row.value === null ? (row.nullLabel ?? '—') : formatValue(row.value, format, money)}
                  {format !== 'percent' ? <span className="rv-bars__share"> · {formatShare(row.share)}</span> : null}
                </span>
                <svg className="rv-bars__track" viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                  <rect className="rv-bars__bg" x={0} y={0} width={100} height={8} rx={2} />
                  {width > 0 ? <rect className="rv-bars__bar" data-testid="share-bar" x={0} y={0} width={width} height={8} rx={2} /> : null}
                </svg>
                {row.detail ? <span className="rv-bars__detail">{row.detail}</span> : null}
              </li>
            )
          })}
        </ol>
      )}
    </figure>
  )
}

/** A short note under a panel: what a figure includes, or why it is missing. */
export function PanelNote({ children }: { children: ReactNode }) {
  return (
    <p className="rv-note" role="note">
      {children}
    </p>
  )
}
